import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { 
  Upload, 
  Download, 
  CheckCircle, 
  XCircle, 
  AlertTriangle,
  Users,
  FileSpreadsheet,
  Loader2
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL || '';

const BulkImportUsers = ({ tenantId, tenantName, onImportComplete }) => {
  const [file, setFile] = useState(null);
  const [validationResult, setValidationResult] = useState(null);
  const [importing, setImporting] = useState(false);
  const [validating, setValidating] = useState(false);
  const [downloadingTemplate, setDownloadingTemplate] = useState(false);
  const [importComplete, setImportComplete] = useState(false);
  const [importSummary, setImportSummary] = useState(null);

  const handleDownloadTemplate = async () => {
    setDownloadingTemplate(true);
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.get(`${API}/api/bulk-import/template/download`, {
        responseType: 'blob',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'template_import_utilisateurs.xlsx');
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      
      toast.success('Template téléchargé avec succès!');
    } catch (error) {
      console.error('Error downloading template:', error);
      toast.error('Erreur lors du téléchargement du template');
    } finally {
      setDownloadingTemplate(false);
    }
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      if (!selectedFile.name.endsWith('.xlsx')) {
        toast.error('Format invalide. Veuillez sélectionner un fichier Excel (.xlsx)');
        return;
      }
      setFile(selectedFile);
      setValidationResult(null);
      setImportComplete(false);
      setImportSummary(null);
    }
  };

  const handleValidate = async () => {
    if (!file) {
      toast.error('Veuillez sélectionner un fichier');
      return;
    }

    setValidating(true);
    setValidationResult(null);

    try {
      const formData = new FormData();
      formData.append('file', file);
      if (tenantId) {
        formData.append('tenant_id', tenantId);
      }

      const token = localStorage.getItem('access_token');
      const response = await axios.post(`${API}/api/bulk-import/validate`, formData, {
        headers: { 
          'Content-Type': 'multipart/form-data',
          'Authorization': `Bearer ${token}`
        }
      });

      setValidationResult(response.data);

      if (response.data.valid) {
        toast.success('✅ Validation réussie! Vous pouvez procéder à l\'import.');
      } else {
        toast.error('❌ Validation échouée. Veuillez corriger les erreurs.');
      }
    } catch (error) {
      console.error('Validation error:', error);
      console.error('Error response:', error.response?.data);
      
      const errorMessage = error.response?.data?.detail || 'Erreur lors de la validation du fichier';
      toast.error(errorMessage);
      
      setValidationResult({
        valid: false,
        errors: [errorMessage]
      });
    } finally {
      setValidating(false);
    }
  };

  const handleImport = async () => {
    if (!file || !validationResult?.valid) {
      toast.error('Veuillez valider le fichier avant l\'import');
      return;
    }

    // Vérifier si des classes doivent être créées
    if (validationResult.missing_classes && validationResult.missing_classes.length > 0) {
      const confirmCreate = window.confirm(
        `Les classes suivantes n'existent pas et seront créées:\n\n${validationResult.missing_classes.join(', ')}\n\nConfirmez-vous la création et l'import?`
      );
      
      if (!confirmCreate) {
        return;
      }
    }

    setImporting(true);

    try {
      const formData = new FormData();
      formData.append('file', file);
      if (tenantId) {
        formData.append('tenant_id', tenantId);
      }
      formData.append('create_missing_classes', validationResult.missing_classes?.length > 0);

      const token = localStorage.getItem('access_token');
      const response = await axios.post(`${API}/api/bulk-import/execute`, formData, {
        headers: { 
          'Content-Type': 'multipart/form-data',
          'Authorization': `Bearer ${token}`
        }
      });

      setImportSummary(response.data.summary);
      setImportComplete(true);
      toast.success(`🎉 Import terminé! ${response.data.summary.students_created} élèves créés`);

      if (onImportComplete) {
        onImportComplete(response.data.summary);
      }
    } catch (error) {
      console.error('Import error:', error);
      const errorDetail = error.response?.data?.detail;
      
      if (typeof errorDetail === 'object' && errorDetail.errors) {
        toast.error('Erreur d\'import: ' + errorDetail.errors.join(', '));
      } else if (typeof errorDetail === 'string') {
        toast.error('Erreur d\'import: ' + errorDetail);
      } else {
        toast.error('Erreur lors de l\'import');
      }
    } finally {
      setImporting(false);
    }
  };

  const handleReset = () => {
    setFile(null);
    setValidationResult(null);
    setImportComplete(false);
    setImportSummary(null);
    // Reset file input
    document.getElementById('file-upload').value = '';
  };

  const downloadCredentials = () => {
    if (!importSummary?.credentials) return;

    const csvContent = [
      ['Rôle', 'Email', 'Mot de passe', 'Nom complet', 'Classe'].join(','),
      ...importSummary.credentials.map(cred => 
        [cred.role, cred.email, cred.password, cred.nom_complet, cred.classe || ''].join(',')
      )
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `identifiants_${tenantName || 'import'}.csv`);
    document.body.appendChild(link);
    link.click();
    link.parentNode.removeChild(link);
    
    toast.success('Fichier des identifiants téléchargé!');
  };

  return (
    <Card className="w-full">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Users className="w-6 h-6" />
          Import en Masse d'Utilisateurs
        </CardTitle>
        <CardDescription>
          {tenantName ? `Établissement: ${tenantName}` : 'Sélectionnez un établissement'}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Étape 1: Télécharger le template */}
        <div className="border rounded-lg p-4 space-y-3">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-semibold flex items-center gap-2">
                <span className="bg-blue-100 text-blue-800 rounded-full w-6 h-6 flex items-center justify-center text-sm">1</span>
                Télécharger le Template
              </h3>
              <p className="text-sm text-gray-600 mt-1">
                Template Excel avec exemples (4 feuilles: ELEVES, PARENTS, ENSEIGNANTS, PERSONNEL_ADMIN)
              </p>
            </div>
            <Button 
              onClick={handleDownloadTemplate}
              disabled={downloadingTemplate}
              variant="outline"
              className="flex items-center gap-2"
            >
              {downloadingTemplate ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Download className="w-4 h-4" />
              )}
              Télécharger Template
            </Button>
          </div>
        </div>

        {/* Étape 2: Upload fichier */}
        <div className="border rounded-lg p-4 space-y-3">
          <div>
            <h3 className="font-semibold flex items-center gap-2 mb-3">
              <span className="bg-blue-100 text-blue-800 rounded-full w-6 h-6 flex items-center justify-center text-sm">2</span>
              Importer le Fichier Rempli
            </h3>
            <div className="flex items-center gap-3">
              <label htmlFor="file-upload" className="cursor-pointer">
                <div className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition">
                  <Upload className="w-4 h-4" />
                  <span className="text-sm">Choisir un fichier</span>
                </div>
                <input
                  id="file-upload"
                  type="file"
                  accept=".xlsx"
                  onChange={handleFileChange}
                  className="hidden"
                />
              </label>
              {file && (
                <div className="flex items-center gap-2">
                  <FileSpreadsheet className="w-4 h-4 text-green-600" />
                  <span className="text-sm font-medium">{file.name}</span>
                  <Badge variant="outline">{(file.size / 1024).toFixed(2)} KB</Badge>
                </div>
              )}
            </div>
          </div>

          {file && !importComplete && (
            <div className="flex gap-2">
              <Button 
                onClick={handleValidate}
                disabled={validating}
                className="flex items-center gap-2"
              >
                {validating ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Validation en cours...
                  </>
                ) : (
                  <>
                    <CheckCircle className="w-4 h-4" />
                    Valider le Fichier
                  </>
                )}
              </Button>
              <Button onClick={handleReset} variant="outline">
                Annuler
              </Button>
            </div>
          )}
        </div>

        {/* Résultats de validation */}
        {validationResult && (
          <div className="space-y-3">
            {/* Statistiques */}
            {validationResult.stats && (
              <div className="grid grid-cols-4 gap-3">
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-center">
                  <div className="text-2xl font-bold text-blue-600">{validationResult.stats.eleves || 0}</div>
                  <div className="text-xs text-blue-800">Élèves</div>
                </div>
                <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-center">
                  <div className="text-2xl font-bold text-green-600">{validationResult.stats.parents || 0}</div>
                  <div className="text-xs text-green-800">Parents</div>
                </div>
                <div className="bg-purple-50 border border-purple-200 rounded-lg p-3 text-center">
                  <div className="text-2xl font-bold text-purple-600">{validationResult.stats.enseignants || 0}</div>
                  <div className="text-xs text-purple-800">Enseignants</div>
                </div>
                <div className="bg-orange-50 border border-orange-200 rounded-lg p-3 text-center">
                  <div className="text-2xl font-bold text-orange-600">{validationResult.stats.personnel_admin || 0}</div>
                  <div className="text-xs text-orange-800">Personnel Admin</div>
                </div>
              </div>
            )}

            {/* Erreurs */}
            {validationResult.errors && validationResult.errors.length > 0 && (
              <Alert variant="destructive">
                <XCircle className="h-4 w-4" />
                <AlertDescription>
                  <div className="font-semibold mb-2">Erreurs de validation:</div>
                  <ul className="list-disc list-inside space-y-1 text-sm">
                    {validationResult.errors.map((error, idx) => (
                      <li key={idx}>{error}</li>
                    ))}
                  </ul>
                </AlertDescription>
              </Alert>
            )}

            {/* Warnings */}
            {validationResult.warnings && validationResult.warnings.length > 0 && (
              <Alert>
                <AlertTriangle className="h-4 w-4" />
                <AlertDescription>
                  <div className="font-semibold mb-2">Avertissements:</div>
                  <ul className="list-disc list-inside space-y-1 text-sm">
                    {validationResult.warnings.map((warning, idx) => (
                      <li key={idx}>{warning}</li>
                    ))}
                  </ul>
                </AlertDescription>
              </Alert>
            )}

            {/* Bouton import */}
            {validationResult.valid && !importComplete && (
              <Button 
                onClick={handleImport}
                disabled={importing}
                className="w-full bg-green-600 hover:bg-green-700"
              >
                {importing ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    Import en cours...
                  </>
                ) : (
                  <>
                    <CheckCircle className="w-4 h-4 mr-2" />
                    Confirmer et Importer
                  </>
                )}
              </Button>
            )}
          </div>
        )}

        {/* Résumé de l'import */}
        {importComplete && importSummary && (
          <div className="border border-green-200 bg-green-50 rounded-lg p-4 space-y-3">
            <div className="flex items-center gap-2 text-green-800 font-semibold">
              <CheckCircle className="w-5 h-5" />
              Import Terminé avec Succès!
            </div>
            
            <div className="grid grid-cols-2 gap-2 text-sm">
              <div className="flex justify-between">
                <span>Élèves créés:</span>
                <span className="font-semibold">{importSummary.students_created}</span>
              </div>
              <div className="flex justify-between">
                <span>Parents créés:</span>
                <span className="font-semibold">{importSummary.parents_created}</span>
              </div>
              <div className="flex justify-between">
                <span>Enseignants créés:</span>
                <span className="font-semibold">{importSummary.teachers_created}</span>
              </div>
              <div className="flex justify-between">
                <span>Personnel admin créé:</span>
                <span className="font-semibold">{importSummary.admin_created}</span>
              </div>
              {importSummary.classes_created > 0 && (
                <div className="flex justify-between col-span-2">
                  <span>Classes créées:</span>
                  <span className="font-semibold">{importSummary.classes_created}</span>
                </div>
              )}
            </div>

            <div className="pt-3 border-t border-green-200">
              <Button 
                onClick={downloadCredentials}
                variant="outline"
                className="w-full"
              >
                <Download className="w-4 h-4 mr-2" />
                Télécharger les Identifiants (CSV)
              </Button>
              <p className="text-xs text-gray-600 mt-2 text-center">
                Mot de passe temporaire: <span className="font-mono font-semibold">EduScope2024!</span>
                <br />
                Les utilisateurs devront le changer à la première connexion
              </p>
            </div>

            <Button onClick={handleReset} variant="outline" className="w-full mt-2">
              Nouveau Import
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default BulkImportUsers;
